import { useState, useEffect } from "react";
import Table from "../../../components/table";
import Button from "../../../components/button";
import { useDispatch, useSelector } from "react-redux";
import { BsInfoCircle } from "react-icons/bs";
import axios from "axios";
import { fetchGames } from "../../../api/endpoint";
import { saveGamesData } from "../../../store/slice/gamesSlice";
import Loader from "../../../components/loader";
import Input from "../../../components/input";
import Select from "react-select";
import GameDetailsModal from "./gameDetailsModal";
import { convertUTCToLocalForGames } from "../../../utils/format";

const skillLevels = [
  { label: "Beginner", value: "BEGINNER" },
  { label: "Intermediate", value: "INTERMEDIATE" },
  { label: "Upper Intermediate", value: "UPPER_INTERMEDIATE" },
  { label: "Proficient", value: "PROFICIENT" },
  { label: "Young Pro Player", value: "YOUNG_PRO_PLAYER" },
  { label: "League Player", value: "LEAGUE_PLAYER" },
  { label: "College Player", value: "COLLEGE_PLAYER" },
  { label: "Pro", value: "PRO" },
];

const gameStatuses = [
  { label: "Active", value: "ACTIVE" },
  { label: "Ended", value: "ENDED" },
  { label: "Draft", value: "DRAFT" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Cancelled", value: "CANCELLED" },
];

const GamesList = () => {
  const dispatch = useDispatch();
  const [filteredGames, setFilteredGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGameId, setSelectedGameId] = useState();
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [filters, setFilters] = useState({
    name: "",
    status: "",
    skillLevels: [],
    sport: "",
    isArchive: false,
  });
  const [isDropdownFilterOpen, setIsDropdownFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const { auth } = useSelector((state) => state.login);
  const { sports } = useSelector((state) => state.sports);

  const fetchGamesList = async (filters) => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        size: pageSize,
        name: filters?.name,
        sport: filters?.sport,
        status: filters?.status,
        isArchive: filters?.isArchive,
      };
      if (filters.skillLevels.length) params.skillLevel = filters.skillLevels.map((s) => s.value);

      const response = await axios.get(fetchGames, {
        headers: { Authorization: `Bearer ${auth}` },
        params,
        paramsSerializer: (params) => {
          return Object.entries(params)
            .map(([key, value]) =>
              Array.isArray(value)
                ? value.map((v) => `${key}=${encodeURIComponent(v)}`).join("&")
                : `${key}=${encodeURIComponent(value)}`,
            )
            .join("&");
        },
      });

      // prepare table data
      const gamesData = response?.data?.content?.map((game) => ({
        sport: game?.sportName,
        name: game?.gameName,
        court: game?.courtName,
        time: convertUTCToLocalForGames(game?.startTime),
        actions: () => getActionButtons(game?.gameId),
      }));

      dispatch(saveGamesData(response?.data?.content));
      setFilteredGames(gamesData);
      setTotalElements(response?.data?.totalElements);
      setTotalPages(response?.data?.totalPages);
    } catch (error) {
      console.error("Error Fetching Games", error);
    } finally {
      setLoading(false);
    }
  };

  const getActionButtons = (gameId) => (
    <button
      className="btn p-0 me-2"
      onClick={() => {
        setSelectedGameId(gameId);
        setIsViewOpen(true);
      }}
    >
      <BsInfoCircle size={20} className="text-primary" />
    </button>
  );

  useEffect(() => {
    fetchGamesList(filters);
  }, [currentPage]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 0 && pageNumber < totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const applyFilters = (e) => {
    e.preventDefault();
    fetchGamesList(filters);
    setIsDropdownFilterOpen(false);
  };

  const clearFilters = (e) => {
    e.preventDefault();
    setFilters({ name: "", status: "", skillLevels: [], sport: "", isArchive: false });
    fetchGamesList({ name: "", status: "", skillLevels: [], sport: "", isArchive: false });
    setIsDropdownFilterOpen(false);
  };

  const headings = [
    { name: "Sport", fieldName: "sport", isSortable: true, sortKey: "none" },
    { name: "Name", fieldName: "name", isSortable: true, sortKey: "none" },
    { name: "Court", fieldName: "court", isSortable: true, sortKey: "none" },
    { name: "Time", fieldName: "time", isSortable: true, sortKey: "none" },
    {
      name: "Actions",
      fieldName: "actions",
      isSortable: false,
      sortKey: "none",
    },
  ];

  const filtersDropdown = () => (
    <form style={{ minWidth: "20rem" }}>
      <div className="row mb-3">
        <label className="col-sm-4 col-form-label">Name:</label>
        <div className="col-sm-8">
          <Input
            type="text"
            placeholder="Enter Game Name"
            value={filters.name}
            onChange={(e) => setFilters({ ...filters, name: e.target.value })}
          />
        </div>
      </div>
      <div className="row mb-3">
        <label className="col-sm-4 col-form-label">Sport:</label>
        <div className="col-sm-8">
          <Input
            type="select"
            options={[
              { label: "Select Sport", value: "" },
              ...sports?.map((sport) => ({
                label: sport?.sportName,
                value: sport?.sportName,
              })),
            ]}
            value={filters.sport}
            onChange={(e) => setFilters({ ...filters, sport: e.target.value })}
          />
        </div>
      </div>
      <div className="row mb-3">
        <label className="col-sm-4 col-form-label">Status:</label>
        <div className="col-sm-8">
          <Input
            type="select"
            options={[{ label: "Select Status", value: "" }, ...gameStatuses]}
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          />
        </div>
      </div>
      <div className="row mb-3">
        <label className="col-sm-4 col-form-label">Skill Level:</label>
        <div className="col-sm-8">
          <Select
            options={skillLevels}
            isMulti
            value={filters.skillLevels}
            onChange={(selected) => setFilters({ ...filters, skillLevels: selected })}
          />
        </div>
      </div>
      <div className="row mb-3">
        <label className="col-sm-4 col-form-label">Archived:</label>
        <div className="col-sm-8">
          <Input
            type="select"
            options={[
              { label: "No", value: false },
              { label: "Yes", value: true },
            ]}
            value={filters.isArchive}
            onChange={(e) => setFilters({ ...filters, isArchive: e.target.value })}
          />
        </div>
      </div>
      <div className="d-flex gap-1 float-end">
        <Button text="Apply Filters" callBack={(e) => applyFilters(e)} />
        <Button text="Clear All" callBack={(e) => clearFilters(e)} />
      </div>
    </form>
  );

  return (
    <div>
      {loading && <Loader />}
      <Table
        headings={headings}
        data={filteredGames}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        enablePagination={totalElements > pageSize}
        filtersDropdown={filtersDropdown}
        isDropdownFilterOpen={isDropdownFilterOpen}
        setIsDropdownFilterOpen={setIsDropdownFilterOpen}
        card={true}
      />
      <GameDetailsModal
        isViewDetailsOpen={isViewOpen}
        closeDetailsModal={() => setIsViewOpen(false)}
        selectedGameId={selectedGameId}
      />
    </div>
  );
};

export default GamesList;
